/**
 * Frontend GST calculation (mirrors backend gstCalc.js).
 *
 * TAX-INCLUSIVE PRICING: rate entered by user INCLUDES GST.
 *   amount = qty * rate  (inclusive of GST)
 *   taxable = amount / (1 + gstRate/100)
 *   tax = amount - taxable
 */

// Seller's GST state. Karnataka is only the fallback — the real value comes from
// BooksCompany.stateCode, which the backend already reads. Initialised once at app
// start via setCompanyStateCode() so the live preview and the saved invoice cannot
// disagree if the company ever moves state.
let COMPANY_STATE_CODE = 29 // Karnataka (fallback)

/** @param {number} code - BooksCompany.stateCode */
export function setCompanyStateCode(code) {
  const n = Number(code)
  if (Number.isInteger(n) && n >= 1 && n <= 38) COMPANY_STATE_CODE = n
}

/** @returns {number} the seller state code currently in effect */
export function getCompanyStateCode() {
  return COMPANY_STATE_CODE
}

/**
 * Calculate tax for a single line item (tax-inclusive).
 * @param {number} gstRate - GST rate (e.g. 5, 12, 18, 28)
 * @param {number} inclusiveAmount - qty * rate (GST-inclusive total)
 * @param {number|null} placeOfSupply - GST state code of buyer
 * @returns {{ taxableAmount: number, cgst: number, sgst: number, igst: number }}
 */
export function calculateLineItemTax(gstRate, inclusiveAmount, placeOfSupply) {
  const amount = round(inclusiveAmount)

  if (Number(gstRate) === 0) {
    return { taxableAmount: amount, cgst: 0, sgst: 0, igst: 0 }
  }

  // Tax is due but nobody has said where the supply took place. The backend throws here;
  // this preview cannot, so it flags instead. Without the flag the panel showed a clean
  // ₹0 GST on an invoice the backend would refuse to approve.
  if (!placeOfSupply) {
    return { taxableAmount: amount, cgst: 0, sgst: 0, igst: 0, unresolved: true }
  }

  const taxable = round(amount / (1 + Number(gstRate) / 100))
  const totalTax = round(amount - taxable)
  const isSameState = Number(placeOfSupply) === COMPANY_STATE_CODE

  if (isSameState) {
    const half = round(totalTax / 2)
    const sgst = round(totalTax - half)
    return { taxableAmount: taxable, cgst: half, sgst, igst: 0 }
  }

  return { taxableAmount: taxable, cgst: 0, sgst: 0, igst: totalTax }
}

export function calculateInvoiceTotals(lineItems) {
  let subtotal = 0, cgst = 0, sgst = 0, igst = 0, total = 0

  for (const item of lineItems) {
    subtotal += Number(item.taxableAmount || 0)
    cgst += Number(item.cgst || 0)
    sgst += Number(item.sgst || 0)
    igst += Number(item.igst || 0)
    total += Number(item.total || 0)
  }

  return { subtotal: round(subtotal), cgst: round(cgst), sgst: round(sgst), igst: round(igst), total: round(total) }
}

/**
 * GST state code encoded in the first two characters of a GSTIN.
 * @param {string|null} gstin
 * @returns {number|null}
 */
function stateCodeFromGstin(gstin) {
  if (!gstin || typeof gstin !== 'string' || gstin.length < 2) return null
  const code = parseInt(gstin.slice(0, 2), 10)
  return Number.isInteger(code) && code >= 1 && code <= 38 ? code : null
}

// Mirrors GST_STATE_CODES and PINCODE_STATE in backend-books/src/services/gstStates.js.
// Duplicated because the two run in different module systems; the two MUST agree, and the
// tests on the backend side are the reference. Misspellings are the ones present in the
// real customer data, imported from Zoho.
const STATE_ALIASES = {
  'jammu and kashmir': 1, 'himachal pradesh': 2, 'punjab': 3, 'chandigarh': 4,
  'uttarakhand': 5, 'haryana': 6, 'delhi': 7, 'new delhi': 7,
  'rajasthan': 8, 'uttar pradesh': 9, 'bihar': 10, 'sikkim': 11,
  'arunachal pradesh': 12, 'nagaland': 13, 'manipur': 14, 'mizoram': 15,
  'tripura': 16, 'meghalaya': 17, 'assam': 18, 'west bengal': 19,
  'jharkhand': 20, 'odisha': 21, 'orissa': 21,
  'chhattisgarh': 22, 'chattisgarh': 22,
  'madhya pradesh': 23, 'gujarat': 24,
  'daman and diu': 26, 'daman': 26, 'dadra and nagar haveli': 26,
  'dadra and nagar haveli and daman and diu': 26,
  'maharashtra': 27, 'maharasthra': 27,
  'karnataka': 29, 'karanataka': 29, 'karantaka': 29, 'karnaaka': 29, 'katnataka': 29,
  'goa': 30, 'lakshadweep': 31, 'kerala': 32,
  'tamil nadu': 33, 'tamilnadu': 33,
  'puducherry': 34, 'pondicherry': 34,
  'andaman and nicobar islands': 35, 'telangana': 36, 'andhra pradesh': 37, 'ladakh': 38,
}

const PINCODE_STATE = {
  160: 4, 194: 38, 248: 5, 249: 5, 403: 30, 607: 33, 737: 11, 744: 35,
  // 246, 247, 396, 605 and 682 removed: measured wrong or shared between states.
  11: 7, 12: 6, 13: 6, 14: 3, 15: 3, 16: 3, 17: 2, 18: 1, 19: 1,
  20: 9, 21: 9, 22: 9, 23: 9, 24: 9, 25: 9, 26: 9, 27: 9, 28: 9,
  30: 8, 31: 8, 32: 8, 33: 8, 34: 8, 36: 24, 37: 24, 38: 24, 39: 24,
  40: 27, 41: 27, 42: 27, 43: 27, 44: 27, 45: 23, 46: 23, 47: 23, 48: 23, 49: 22,
  56: 29, 57: 29, 58: 29, 59: 29, 60: 33, 61: 33, 62: 33, 63: 33, 64: 33,
  67: 32, 68: 32, 69: 32, 70: 19, 71: 19, 72: 19, 73: 19, 74: 19,
  75: 21, 76: 21, 77: 21, 78: 18,
  // Absent on purpose: 50-53, 79, 80-85 are shared between states.
}

const ALIAS_KEYS = Object.keys(STATE_ALIASES).sort((a, b) => b.length - a.length)

/**
 * GST state code for a free-text address block. Mirror of stateCodeFromAddress in
 * backend-books/src/services/gstCalc.js — the reasoning is written up there. The two
 * signals must AGREE or this returns null; silence beats a confident wrong answer.
 * @param {string|null} text
 * @returns {number|null}
 */
export function stateCodeFromAddress(text) {
  if (!text) return null

  const hay = ` ${String(text).toLowerCase().replace(/[^a-z0-9]+/g, ' ')} `
  let named = null
  for (const k of ALIAS_KEYS) {
    const at = hay.lastIndexOf(` ${k} `)
    if (at === -1) continue
    if (!named || at > named.at) named = { at, code: STATE_ALIASES[k] }
  }

  let byPin = null
  const pins = String(text).match(/\b[1-9][0-9]{5}\b/g)
  if (pins) {
    const pin = pins[pins.length - 1]
    byPin = PINCODE_STATE[Number(pin.slice(0, 3))] || PINCODE_STATE[Number(pin.slice(0, 2))] || null
  }

  if (named && byPin) return named.code === byPin ? named.code : null
  return named ? named.code : byPin
}

/**
 * Does a place of supply contradict the document it sits on?
 *
 * Mirror of placeOfSupplyConflict in backend-books/src/services/gstCalc.js, and the
 * ladder must match it rung for rung. The Bill To typed on THIS invoice wins over the
 * customer master — reading the master instead is what made a first version of this
 * check "correct" two already-correct invoices.
 *
 * @param {number|string|null} placeOfSupply
 * @param {object|null} customer
 * @param {{ billToAddress?: string|null }} [doc]
 * @returns {{ expected: number, source: string }|null}
 */
export function placeOfSupplyConflict(placeOfSupply, customer, doc = {}) {
  const pos = Number(placeOfSupply)
  if (!Number.isInteger(pos) || pos < 1 || pos > 38) return null

  const ladder = [
    [stateCodeFromAddress(doc?.billToAddress), 'the Bill To address on this invoice'],
    [stateCodeFromGstin(customer?.gstin), "the customer's GSTIN"],
    [stateCodeFromAddress(customer?.billingAddress), "the customer's billing address"],
    [stateCodeFromAddress(customer?.billingState), "the customer's state"],
  ]
  for (const [code, source] of ladder) {
    if (!code) continue
    return code === pos ? null : { expected: code, source }
  }
  return null
}

function round(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100
}
